import { BaseService } from 'core/services';
import { findParams } from 'core/utils';
import { TrackingDashboardRouteService } from 'common/data';

const SUMMARY_EMBED = [
	'warehouse',
	'seller',
	'carrier',
	'carrier.carrierType',
	'carrierDeliveryStatus',
	'carrierDeliveryStatus.shipmentInfoDeliveryStatus',
];

// Map a ShipmentInfoDeliveryStatus.SystemName to one of our tracking state tokens.
// SystemName (not Abrv, which is a %20-encoded localisation slug like "carrier%20en%20route")
// is the stable machine-readable key — see ShipmentInfoDeliveryStatusSystemNames on the BE.
// Used by getShipmentsSummary, which reads SystemName straight off the dashboard SQL row.
function systemNameToTrackingStatus(raw) {
	const systemName = (raw || '').toLowerCase();

	if (systemName === 'delivered') return 'delivered';

	if (systemName === 'onvehiclefordelivery') return 'onVehicleForDelivery';

	// "Arrived at carrier facility" is still moving through the carrier network — bucket with en route.
	if (systemName === 'carrierenroute' || systemName === 'arrivedatcarrierfacility' || systemName === "intransit") return 'enRoute';

	if (systemName === 'exception') return 'exception';

	// "AwaitingCarrierPickup", empty, or any unrecognised status → awaiting pickup
	return 'awaitingPickup';
}

// Normalise an Abrv slug, stripping Baasic's literal "%20" word-separator as well as
// real whitespace/hyphens, so "carrier%20en%20route" and "carrier en route" both compare equal.
function normalizeAbrv(val) {
	return (val || '').toLowerCase().replace(/%20/g, '').replace(/[-\s]/g, '');
}

// Map a ShipmentInfoDeliveryStatus.Abrv slug to one of our tracking state tokens.
// Used by getShipments (embedded Baasic REST response), whose ShipmentInfoDeliveryStatusREST
// model only exposes Abrv/Name — SystemName isn't serialised on that endpoint.
function abrvToTrackingStatus(raw) {
	const abrv = normalizeAbrv(raw);

	if (abrv === 'delivered') return 'delivered';

	if (abrv === 'onvehiclefordelivery') return 'onVehicleForDelivery';

	if (abrv === 'carrierenroute' || abrv === 'arrivedatcarrierfacility') return 'enRoute';

	if (abrv === 'exception') return 'exception';

	// "awaitingcarrierpickup", empty, or any unrecognised status → awaiting pickup
	return 'awaitingPickup';
}

// Used by getShipments (embedded Baasic response format).
function toTrackingStatus(shipment) {
	const raw =
		shipment.carrierDeliveryStatus &&
		shipment.carrierDeliveryStatus.shipmentInfoDeliveryStatus &&
		shipment.carrierDeliveryStatus.shipmentInfoDeliveryStatus.abrv
			? shipment.carrierDeliveryStatus.shipmentInfoDeliveryStatus.abrv
			: '';
	return abrvToTrackingStatus(raw);
}

function transformShipment(s) {
	// shipment-infos uses applicationTitle for both warehouse and seller display names
	const warehouseName = s.warehouse ? s.warehouse.applicationTitle : null;
	const sellerName = s.seller ? s.seller.applicationTitle : null;

	const carrier = s.carrier
		? s.carrier.carrierType
			? s.carrier.carrierType.name
			: s.carrier.name || null
		: null;

	return {
		warehouseId: s.warehouseId,
		warehouseName,
		sellerId: s.sellerId,
		sellerName,
		trackingStatus: toTrackingStatus(s),
		trackingNumber: s.trackingNumber,
		carrier,
		lastUpdate: s.dateUpdated || s.dateCreated,
	};
}

class TrackingDashboardService extends BaseService {
	constructor(apiClient) {
		super(apiClient, new TrackingDashboardRouteService());
	}

	getShipmentsSummary = async (dateRange) => {
		const filter = {
			shippedDateFrom: dateRange && dateRange.from ? dateRange.from : undefined,
			shippedDateTo: dateRange && dateRange.to ? dateRange.to : undefined,
		};
		const url = this.routeService.shipmentsSummary(filter);
		const res = await this.apiClient.get(url);
		const items = Array.isArray(res.data) ? res.data : [];
		return {
			data: items.map(s => {
				const row = {
					id: s.id || null,
					fulfillmentOrderId: s.fulfillmentOrderId || null,
					warehouseId: s.warehouseId,
					warehouseName: s.warehouseName || null,
					sellerId: s.sellerId,
					sellerName: s.sellerName || null,
					trackingStatus: systemNameToTrackingStatus(s.deliveryStatusSystemName),
					trackingNumber: s.trackingNumber,
					carrier: s.carrier || null,
					lastUpdate: s.lastUpdate,
					labelCreatedDate: s.labelCreatedDate || null,
				};
				return row;
			}),
		};
	};

	getShipments = async filter => {
		const requestFilter = {
			embed: SUMMARY_EMBED,
			warehouseId: filter.warehouseId,
			sellerId: filter.sellerId,
			shippedDateFrom: filter.from || undefined,
			shippedDateTo: filter.to || undefined,
			rpp: 1000,
			page: 1,
		};
		const url = this.routeService.shipments();
		const res = await this.apiClient.post(url, findParams(requestFilter));
		const raw = res.data;
		const items = raw && raw.item ? raw.item : Array.isArray(raw) ? raw : [];
		let all = items.map(transformShipment);
		if (filter.status) all = all.filter(s => s.trackingStatus === filter.status);
		if (filter.carrier) all = all.filter(s => s.carrier === filter.carrier);
		return { data: all };
	};
}

export default TrackingDashboardService;